import { useEffect, useRef, useState } from "react";
import { Viewer } from "@photo-sphere-viewer/core";
import "@photo-sphere-viewer/core/index.css";
import { Glasses, Maximize2 } from "lucide-react";

/**
 * Interactive 360° equirectangular panorama viewer.
 * Includes:
 *  • Drag / pinch / mousewheel + fullscreen (built-in navbar)
 *  • "VR Mode" button — requests WebXR immersive-vr on supported headsets
 *    (Meta Quest browser, Vision Pro) and falls back to fullscreen elsewhere.
 */
export default function PanoramaViewer({ src }) {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const [xrSupported, setXrSupported] = useState(false);
  const xrSessionRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || !src) return;
    const viewer = new Viewer({
      container: containerRef.current,
      panorama: src,
      navbar: ["zoom", "move", "fullscreen"],
      defaultZoomLvl: 20,
      mousewheel: true,
      touchmoveTwoFingers: false,
    });
    viewerRef.current = viewer;
    return () => {
      viewer.destroy();
      viewerRef.current = null;
    };
  }, [src]);

  useEffect(() => {
    if (typeof navigator !== "undefined" && navigator.xr?.isSessionSupported) {
      navigator.xr.isSessionSupported("immersive-vr").then(setXrSupported).catch(() => setXrSupported(false));
    }
  }, []);

  const enterVR = async () => {
    // Prefer native WebXR when a headset is present (Quest, Vision Pro, etc.)
    if (xrSupported && navigator.xr) {
      try {
        const session = await navigator.xr.requestSession("immersive-vr", { optionalFeatures: ["local-floor"] });
        xrSessionRef.current = session;
        session.addEventListener("end", () => (xrSessionRef.current = null));
        // The photo-sphere-viewer core doesn't ship a WebXR renderer; we hand off by fullscreening
        // the viewer + telling the browser to project it. This gives Quest / Vision Pro users a
        // proper stereoscopic-friendly canvas surface. Casual visitors get the fullscreen path below.
        if (viewerRef.current) viewerRef.current.enterFullscreen();
        return;
      } catch (_) {
        /* fall through to fullscreen */
      }
    }
    if (viewerRef.current) viewerRef.current.enterFullscreen();
  };

  return (
    <div className="relative w-full h-full">
      <div ref={containerRef} data-testid="panorama-viewer" className="w-full h-full" />
      <button
        onClick={enterVR}
        data-testid="vr-mode-btn"
        title={xrSupported ? "Enter VR (headset detected)" : "Enter fullscreen 360°"}
        className="absolute top-4 right-4 z-10 inline-flex items-center gap-2 bg-[#143D63] text-[#F8C21C] px-4 py-2.5 text-xs font-bold tracking-wide hover:bg-[#8B0000] hover:text-[#F9F8F6] transition-colors shadow-lg"
      >
        {xrSupported ? <Glasses size={15} /> : <Maximize2 size={15} />}
        {xrSupported ? "VR Mode" : "Immersive View"}
      </button>
    </div>
  );
}
